import { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { getEnergyBatteryCapacity } from '../utils/chartAPI';

const round2 = (v) => (v == null ? null : +parseFloat(v).toFixed(2));

export default function useEnergyBattery({
  site,
  msSessionInfo,
  startDate,
  endDate,
  inView,
  refreshKey,
}) {
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState([]);
  const [domainStartMs, setDomainStartMs] = useState(undefined);
  const [domainEndMs, setDomainEndMs] = useState(undefined);
  const hasFetched = useRef(false);

  useEffect(() => {
    hasFetched.current = false;
  }, [refreshKey, startDate?.valueOf?.(), endDate?.valueOf?.()]);

  useEffect(() => {
    if (!inView || !site?.id || !msSessionInfo) return;
    if (refreshKey === undefined && hasFetched.current) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const start = startDate ? dayjs(startDate) : dayjs().startOf('day');
        let end = endDate ? dayjs(endDate) : start.add(1, 'day').subtract(1, 'minute');
        if (!end.isAfter(start)) end = start.add(1, 'minute');

        const raw =
          (await getEnergyBatteryCapacity(site, start, end, msSessionInfo)) || {};

        const energyPts = (raw.energy ?? [])
          .map((p) => ({ x: dayjs(p.time).valueOf(), y: round2(p.value) }))
          .filter((pt) => Number.isFinite(pt.y));

        const capacityPts = (raw.online_battery_capacity ?? [])
          .map((p) => ({ x: dayjs(p.time).valueOf(), y: round2(p.value) }))
          .filter((pt) => Number.isFinite(pt.y));

        setSeries([
          { name: 'Energy Available', type: 'line', data: energyPts },
          { name: 'Online Battery Capacity', type: 'line', data: capacityPts },
        ]);

        setDomainStartMs(start.valueOf());
        setDomainEndMs(end.valueOf());
        hasFetched.current = true;
      } catch (err) {
        console.error('EnergyBattery fetch error:', err);
        setSeries([
          { name: 'Energy Available', type: 'line', data: [] },
          { name: 'Online Battery Capacity', type: 'line', data: [] },
        ]);
        setDomainStartMs(undefined);
        setDomainEndMs(undefined);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    inView,
    site?.id,
    msSessionInfo,
    startDate?.valueOf?.(),
    endDate?.valueOf?.(),
    refreshKey,
  ]);

  return { loading, series, domainStartMs, domainEndMs };
}
